import { cn } from "cn";
import { Typography } from "@/components/ui/typography";
import { PaymentMethodIcon } from "@/features/shared/billing/payments/_components/payment-method-icon";
import type { PaymentMethodType } from "@/features/shared/billing/payments/_types/payment-method-type";

type PaymentMethodCardProps = {
  /** The card brand name or bank name. */
  brand: string;

  /** The last 4 digits of the card or account number. */
  last4: string;

  /** The card's expiration month (cards only). */
  expMonth?: number;

  /** The card's expiration year (cards only). */
  expYear?: number;

  /** The payment method type: "card" or "us_bank_account". */
  type: PaymentMethodType;

  /** The bank account type, e.g. "checking" (bank accounts only). */
  accountType?: string;

  /** Additional CSS classes for the card container. */
  className?: string;
};

/**
 * A visual card component that renders a credit card or bank account in a card-like format,
 * displaying the brand icon, masked number, and expiration date or account type.
 *
 * @param brand - The card brand name or bank name.
 * @param last4 - The last 4 digits of the card or account number.
 * @param expMonth - The card's expiration month (cards only).
 * @param expYear - The card's expiration year (cards only).
 * @param type - The payment method type: "card" or "us_bank_account".
 * @param accountType - The bank account type, e.g. "checking" (bank accounts only).
 * @param className - Additional CSS classes for the card container.
 */
export const PaymentMethodCard = ({
  brand,
  last4,
  expMonth,
  expYear,
  type,
  accountType,
  className,
}: PaymentMethodCardProps) => {
  return (
    <div
      className={cn(
        /* sorted */
        "flex",
        "flex-col gap-20",
        "w-full",
        "p-4",
        "rounded-2xl border border-border-dark-low shadow-lg bg-bg-surface-elevate-low bg-linear-[107deg] from-transparent to-bg-container",
        className,
      )}
    >
      <div className="flex flex-col items-end">
        <PaymentMethodIcon brand={brand} type={type} />
      </div>

      <div className="flex flex-col gap-0.5 text-base">
        <Typography size="base" className="font-semibold text-text-dark-max">
          {brand}
        </Typography>
        <div className="flex items-center justify-between">
          <Typography size="base" className="text-text-dark-high">
            •••• {last4}
          </Typography>
          {type === "card" && expMonth != null && expYear != null && (
            <Typography size="base" className="text-text-dark-high">
              Exp. {String(expMonth).padStart(2, "0")}/{expYear}
            </Typography>
          )}
          {type === "us_bank_account" && accountType && (
            <Typography size="base" className="text-text-dark-high">
              {accountType.charAt(0).toUpperCase() +
                accountType.slice(1).toLowerCase()}
            </Typography>
          )}
        </div>
      </div>
    </div>
  );
};
